import { useState } from 'react';
import { toast } from 'react-toastify';
import ThemeToggle from '../components/ThemeToggle';
import { submitContactMessage } from '../services/contactService';
import '../components/DataPage.css';
import './Landing.css';

const FEATURES = [
  {
    title: 'Easy & Effective',
    description:
      'Build polished emails with a drag-and-drop editor. Start from templates or a blank canvas — no design or coding experience needed.',
  },
  {
    title: 'Smart & Automated',
    description:
      'Trigger campaigns from customer actions, preferences, or purchases so the right message lands at the right time.',
  },
  {
    title: 'All-in-One & Affordable',
    description:
      'Manage contacts, segment audiences, track results, and reach people over email, SMS, WhatsApp, or chat — in one place, at a price that fits.',
  },
  {
    title: 'Real-time & Insightful',
    description:
      'Watch opens, clicks, and conversions as they happen with clear reporting that shows what is working — and what to improve next.',
  },
  {
    title: 'Precise & Targeted',
    description:
      'Group subscribers by behavior and interests so every send feels personal, relevant, and more likely to convert.',
  },
  {
    title: 'Reliable & Scalable',
    description:
      'Schedule campaigns, manage sending capacity, and grow with confidence as your list and volume expand.',
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NAME_LENGTH = 120;
const MAX_MESSAGE_LENGTH = 5000;

function Landing({ onLoginClick }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) {
      return;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearFieldError = (field) => {
    setSubmitSuccess(false);
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validateContactForm = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    const errors = {};

    if (!trimmedName) {
      errors.name = 'Name is required.';
    } else if (trimmedName.length > MAX_NAME_LENGTH) {
      errors.name = `Name must be ${MAX_NAME_LENGTH} characters or less.`;
    }

    if (!trimmedEmail) {
      errors.email = 'Email is required.';
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      errors.email = 'Enter a valid email address (e.g. you@example.com).';
    }

    if (!trimmedMessage) {
      errors.message = 'Message is required.';
    } else if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      errors.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or less.`;
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return null;
    }

    return {
      name: trimmedName,
      email: trimmedEmail,
      message: trimmedMessage,
    };
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const payload = validateContactForm();
    if (!payload) {
      return;
    }

    setSubmitting(true);
    setSubmitSuccess(false);

    try {
      await submitContactMessage(payload);
      setName('');
      setEmail('');
      setMessage('');
      setFieldErrors({});
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitSuccess(false);
      toast.error(err.message || 'Could not send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="landing-page"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/ccchaos.svg)`,
      }}
    >
      <header className="landing-page__nav">
        <div className="landing-page__nav-inner">
          <a
            className="landing-page__brand"
            href="#top"
            onClick={(event) => {
              event.preventDefault();
              scrollToSection('top');
            }}
          >
            <span className="landing-page__logo">
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" />
            </span>
            <span className="landing-page__brand-name">EKDANTAYA</span>
          </a>

          <div className="landing-page__nav-actions">
            <ThemeToggle />
            <button
              type="button"
              className="landing-page__btn landing-page__btn--nav"
              onClick={onLoginClick}
            >
              Login
            </button>
          </div>
        </div>
      </header>

      <main className="landing-page__main" id="top">
        <section className="landing-page__hero">
          <div className="landing-page__section-card landing-page__hero-card">
            <h1>Simplify Your Email Marketing</h1>
            <p>
              Powerful tools to reach your audience, analyze performance, and grow
              your business with confidence.
            </p>
            <button
              type="button"
              className="landing-page__btn"
              onClick={() => scrollToSection('contact')}
            >
              Get Started
            </button>
          </div>
        </section>

        <section className="landing-page__features" id="features">
          <div className="landing-page__section-card">
            <h2 className="landing-page__features-heading">
              Everything you need to turn emails into revenue
            </h2>
            <div className="landing-page__features-grid">
              {FEATURES.map((feature) => (
                <article key={feature.title} className="landing-page__feature-card">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-page__contact" id="contact">
          <form
            className="landing-page__section-card landing-page__contact-card"
            onSubmit={handleContactSubmit}
            noValidate
          >
            <h2>Get in Touch</h2>

            <label className="data-page__modal-field" htmlFor="landing-name">
              <span>Name</span>
              <input
                id="landing-name"
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  clearFieldError('name');
                }}
                autoComplete="name"
                required
                maxLength={MAX_NAME_LENGTH}
                aria-invalid={Boolean(fieldErrors.name)}
                aria-describedby={fieldErrors.name ? 'landing-name-error' : undefined}
                disabled={submitting}
              />
              {fieldErrors.name && (
                <p id="landing-name-error" className="data-page__field-error">
                  {fieldErrors.name}
                </p>
              )}
            </label>

            <label className="data-page__modal-field" htmlFor="landing-email">
              <span>Email</span>
              <input
                id="landing-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearFieldError('email');
                }}
                autoComplete="email"
                required
                inputMode="email"
                aria-invalid={Boolean(fieldErrors.email)}
                aria-describedby={fieldErrors.email ? 'landing-email-error' : undefined}
                disabled={submitting}
              />
              {fieldErrors.email && (
                <p id="landing-email-error" className="data-page__field-error">
                  {fieldErrors.email}
                </p>
              )}
            </label>

            <label className="data-page__modal-field" htmlFor="landing-message">
              <span>Message</span>
              <textarea
                id="landing-message"
                placeholder="How can we help?"
                rows={5}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  clearFieldError('message');
                }}
                required
                maxLength={MAX_MESSAGE_LENGTH}
                aria-invalid={Boolean(fieldErrors.message)}
                aria-describedby={fieldErrors.message ? 'landing-message-error' : undefined}
                disabled={submitting}
              />
              {fieldErrors.message && (
                <p id="landing-message-error" className="data-page__field-error">
                  {fieldErrors.message}
                </p>
              )}
            </label>

            <button
              type="submit"
              className="landing-page__btn landing-page__btn--submit"
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitSuccess && (
              <p className="landing-page__contact-success" role="status" aria-live="polite">
                Thanks! We will get back to you soon.
              </p>
            )}
          </form>
        </section>
      </main>

      <footer className="landing-page__footer">
        <p>© {new Date().getFullYear()} EKDANTAYA. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Landing;
